import { useMemo } from 'react'
import {
  PopoverHorizontalPositionProps,
  PopoverVerticalPositionProps
} from '@dcxwam/dcx-wam-ui-foundations'
import {
  DecoratedUserTypes,
  UserTypes
} from '../../../../services/user/user-data'
import {
  CORESITE_LOCALSTORAGE,
  HYBRIS_ACCESS_TOKEN
} from '../../../../utils/constants'
import {
  pushDataLayerMiniCartSeeCartClick,
  pushDataLayerMiniCartValidateOrderClick
} from '../../common/datalayer/methods'

const outOfStockValue = 'outOfStock'

export const getStockInfoLabel = ({ ...props }) => {
  const { isSsr, stockLevelStatus, product } = props
  let stockLabel: string | undefined = '&nbsp;' // Default value to keep the line and avoid CLS
  switch (!isSsr && stockLevelStatus) {
    case 'inStock':
      stockLabel = product?.inStockLabel
      break
    case outOfStockValue:
      stockLabel = product?.outOfStockLabel
      break
    case 'lowStock':
      stockLabel = product?.lowStockLabel
      break
    default:
      break
  }
  return stockLabel
}

export const getAddToCartButtonLabel = ({
  guestAddToCartEnabled,
  preOrderUntilDate,
  stockLevelStatus,
  addToCartLabel,
  preOrderLabel,
  notifyMe,
  notifyMeCTALabel,
  outOfStockLabel,
  loginRequiredCTALabel,
  userData
}: {
  guestAddToCartEnabled: boolean | undefined
  preOrderUntilDate: string | undefined
  stockLevelStatus: string | undefined
  addToCartLabel: string | undefined
  preOrderLabel: string | undefined
  notifyMe: boolean | undefined
  notifyMeCTALabel: string | undefined
  outOfStockLabel: string | undefined
  loginRequiredCTALabel: string | undefined
  userData: {
    user: object
    decoratedUserType: DecoratedUserTypes | null
  }
}) => {
  const isGuestAddToCart =
    guestAddToCartEnabled || userData?.decoratedUserType !== UserTypes.Las

  const isPreorder =
    (preOrderUntilDate?.replace('T', '') as unknown as Date) >= new Date()

  const isButtonInDefaultState =
    !isPreorder &&
    stockLevelStatus &&
    stockLevelStatus !== outOfStockValue &&
    isGuestAddToCart

  let buttonLabel: string | undefined = ''

  if (isButtonInDefaultState) {
    buttonLabel = addToCartLabel
  }

  if (isPreorder && isGuestAddToCart) {
    buttonLabel = preOrderLabel
  }

  if (
    isGuestAddToCart &&
    !isPreorder &&
    notifyMe &&
    stockLevelStatus === outOfStockValue
  ) {
    buttonLabel = notifyMeCTALabel
  }

  if (
    !isPreorder &&
    !notifyMe &&
    (stockLevelStatus === outOfStockValue || !stockLevelStatus)
  ) {
    buttonLabel = outOfStockLabel
  }

  if (
    !isGuestAddToCart &&
    (isPreorder || notifyMe || stockLevelStatus !== outOfStockValue)
  ) {
    buttonLabel = loginRequiredCTALabel
  }

  return buttonLabel
}

export const useMiniCartConfig = ({
  productEntry,
  config,
  variants,
  cartLocalStorageJSON,
  activeMiniCart,
  setActiveMiniCart,
  productQuantityLabel
}) => {
  const addedProductCode = productEntry?.product?.code
  const propsOfAddedProduct =
    addedProductCode &&
    variants?.find(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (variant: any) => variant?.globalArticleCode === addedProductCode
    )

  const addedProductBadges =
    productEntry?.product?.productAvailableBadges?.productAvailableBadge
      ?.length > 0 &&
    productEntry?.product?.productAvailableBadges?.productAvailableBadge?.map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (badgeProps: any) => ({
        label: badgeProps.badge.name,
        background: badgeProps?.badge?.color?.hexCode
      })
    )

  const productImageProps = {
    height: '100',
    width: '100',
    src: `${propsOfAddedProduct?.assets?.[0].assetFullUrl}?width=100`
  }

  const configPopoverMiniCart = useMemo(
    () => ({
      open: activeMiniCart,
      verticalPositionAnchor: PopoverVerticalPositionProps.Top,
      horizontalPositionAnchor: PopoverHorizontalPositionProps.Right,
      verticalTransform: PopoverVerticalPositionProps.Top,
      horizontalTransform: PopoverHorizontalPositionProps.Center
    }),
    [activeMiniCart]
  )

  const priceInfoProps = {
    ...(productEntry?.totalPrice?.formattedValue ===
    productEntry?.totalWithoutDiscounts?.formattedValue
      ? {
          currentPriceTextProps: {
            content: productEntry?.totalPrice?.formattedValue
          }
        }
      : {
          currentPriceTextProps: {
            content: productEntry?.totalPrice?.formattedValue
          },
          originalPriceTextProps: {
            content: productEntry?.totalWithoutDiscounts.formattedValue
          }
        })
  }

  return {
    popoverProps: configPopoverMiniCart,
    title: config?.successMessage,
    onClose: () => setActiveMiniCart(false),
    ...(addedProductBadges
      ? { productChipsProps: { chipPropsList: addedProductBadges } }
      : {}),
    priceInfoProps,
    ...(propsOfAddedProduct?.assets?.length > 0 ? { productImageProps } : {}),
    productName: productEntry?.product?.name,
    productVariantName: productEntry?.product?.nuance?.description,
    productQuantity: productEntry?.quantity,
    productQuantityLabel,
    actions: [
      {
        label: config?.checkoutCTALabel,
        linkUrl: config?.checkoutCTAUrl,
        onClick: pushDataLayerMiniCartValidateOrderClick
      },
      {
        label: `${config?.cartCTALabel} (${cartLocalStorageJSON?.cart?.totalUnitCount})`,
        linkUrl: config?.cartCTAUrl,
        onClick: pushDataLayerMiniCartSeeCartClick
      }
    ],
    idleCloseTimeout: 5000,
    blurCloseTimeout: 3000,
    'aria-label': config?.closeButtonAriaLabel
  }
}

export const createAddToCartMethodConfig = ({ ...props }) => {
  const {
    orderType,
    setIsButtonDisabled,
    setIsButtonLoading,
    dataLayerEcommerceProduct,
    productData,
    productID,
    caConfig,
    setActiveMiniCart,
    cookies,
    setCookie,
    cartLocalStorageJSON,
    setCartLocalStorage,
    setErroMessageContent,
    setDialogErrorMessageIsOpen,
    userData,
    productQuantity
  } = props

  const { hybrisConfig, shopConfig, market, lang, muleSoftConfig } = caConfig

  const CA_HYBRIS_URL = hybrisConfig?.hybrisUrl
  const CA_CLIENT_ID = hybrisConfig?.hybrisClientId
  const CA_CLIENT_SECRET = hybrisConfig?.hybrisClientSecret
  const CA_HYBRIS_BASE_SITE_ID = hybrisConfig?.baseSiteId
  const CA_HYBRIS_LANGUAGE = hybrisConfig?.language

  return {
    orderTypes: orderType,
    setIsButtonDisabled,
    setIsButtonLoading,
    setErroMessageContent,
    isLoginUser: userData?.decoratedUserType !== UserTypes.Las,
    errorContent: {
      exceededMaximumQuantityTitle: shopConfig?.exceededMaximumQuantityTitle,
      exceededMaximumQuantityMessage:
        shopConfig?.exceededMaximumQuantityMessage,
      differentOrderTypeTitle: shopConfig?.differentOrderTypeTitle,
      differentOrderTypeMessage: shopConfig?.differentOrderTypeMessage,
      differentLoyaltyRequiredTitle: shopConfig?.differentLoyaltyRequiredTitle,
      differentLoyaltyRequiredMessage:
        shopConfig?.differentLoyaltyRequiredMessage,
      differentOrderTypeConfirmCTALabel:
        shopConfig?.differentOrderTypeConfirmCTALabel,
      loginRequiredTitle: shopConfig?.loginRequiredTitle,
      loginRequiredMessage: shopConfig?.loginRequiredMessage,
      loginRequiredCTALabel: shopConfig?.loginRequiredCTALabel,
      loginPageURL: shopConfig?.loginPageURL,
      addToCartDialogCloseAriaLabel: shopConfig?.addToCartDialogCloseAriaLabel,
      addToCartDialogCancelCTALabel: shopConfig?.addToCartDialogCancelCTALabel
    },
    productID,
    productQuantity,
    currentProductVariant: productData,
    hybrisConfig: {
      CA_HYBRIS_URL,
      CA_CLIENT_ID,
      CA_CLIENT_SECRET,
      CA_HYBRIS_BASE_SITE_ID,
      CA_HYBRIS_LANGUAGE,
      HYBRIS_ACCESS_TOKEN
    },
    defaultErrorMessage: shopConfig?.addToCartErrorMessage,
    mulesoftConfig: { ...muleSoftConfig },
    market,
    dataLayerEcommerceProduct,
    setCookie,
    cookies,
    CORESITE_LOCALSTORAGE,
    language: lang,
    setDialogErrorMessageIsOpen,
    cartLocalStorageJSON,
    setCartLocalStorage,
    setActiveMiniCart
  }
}
